import { z } from 'zod';

// Queue name constant shared between api and worker packages.
export const POI_IMPORT_QUEUE_NAME = 'poi-import';

// ─── POI schema ──────────────────────────────────────────────────────────────
//
// Mirrors the pois table. raw_tags is intentionally excluded per spec.
// location is transformed to { lat, lng } for API consumers.

export const PoiSchema = z.object({
  id: z.string().uuid(),
  osmKey: z.string(),
  osmValue: z.string(),
  name: z.string(),
  brand: z.string().nullable(),
  brandWikidata: z.string().nullable(),
  // raw_tags excluded per spec
  location: z.object({ lat: z.number(), lng: z.number() }),
  source: z.enum(['osm', 'custom']),
  sourceId: z.string().nullable(),
  internalOnly: z.boolean(),
  deletedAt: z.string().datetime().nullable(),
  createdBy: z.number().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Poi = z.infer<typeof PoiSchema>;

// ─── Create POI body ─────────────────────────────────────────────────────────

export const CreatePoiBodySchema = z.object({
  osm_key: z.string().min(1),
  osm_value: z.string().min(1),
  name: z.string().min(1),
  location: z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
  }),
  internal_only: z.boolean().optional(),
});

export type CreatePoiBody = z.infer<typeof CreatePoiBodySchema>;

// ─── Patch POI body ──────────────────────────────────────────────────────────

export const PatchPoiBodySchema = z.object({
  internal_only: z.boolean(),
});

export type PatchPoiBody = z.infer<typeof PatchPoiBodySchema>;

// ─── POI list query ──────────────────────────────────────────────────────────

/**
 * Coerces query param booleans correctly:
 * - "true" -> true, "false" -> false, "1" -> true, "0" -> false
 * - z.coerce.boolean() converts "false" to true (non-empty string), so we use a transform instead.
 */
const queryBoolean = z
  .string()
  .transform((v) => v === 'true' || v === '1')
  .or(z.boolean())
  .optional();

export const PoiListQuerySchema = z.object({
  bbox: z.string().optional(),
  osm_key: z.string().optional(),
  osm_value: z.string().optional(),
  source: z.enum(['osm', 'custom']).optional(),
  internal_only: queryBoolean,
  include_deleted: queryBoolean,
});

export type PoiListQuery = z.infer<typeof PoiListQuerySchema>;

// ─── Nearby POI ──────────────────────────────────────────────────────────────

export const NearbyPoiSchema = z.object({
  poi_id: z.string().uuid(),
  osm_key: z.string(),
  osm_value: z.string(),
  name: z.string(),
  brand: z.string().nullable(),
  distance_m: z.number(),
});

export type NearbyPoi = z.infer<typeof NearbyPoiSchema>;

// ─── POI category ────────────────────────────────────────────────────────────

export const PoiCategorySchema = z.object({
  id: z.number(),
  osm_key: z.string(),
  osm_value: z.string(),
});

export type PoiCategory = z.infer<typeof PoiCategorySchema>;

// ─── POI stats (admin dashboard) ─────────────────────────────────────────────

export const PoiCategoryCountSchema = z.object({
  osm_key: z.string(),
  osm_value: z.string(),
  count: z.number(),
});

export const PoiStatsSchema = z.object({
  totalOsm: z.number(),
  totalCustom: z.number(),
  totalDeleted: z.number(),
  lastImportedAt: z.string().datetime().nullable(),
  categoryCounts: z.array(PoiCategoryCountSchema),
});

export type PoiStats = z.infer<typeof PoiStatsSchema>;
export type PoiCategoryCount = z.infer<typeof PoiCategoryCountSchema>;

// ─── POI import trigger ───────────────────────────────────────────────────────

export const PoiImportTriggerResponseSchema = z.object({
  jobId: z.string(),
});

export type PoiImportTriggerResponse = z.infer<typeof PoiImportTriggerResponseSchema>;

export const PoiImportStatusSchema = z.object({
  status: z.enum(['idle', 'running', 'completed', 'failed']),
  lastRunAt: z.string().datetime().nullable(),
  lastError: z.string().nullable(),
  lastJobId: z.string().nullable(),
});

export type PoiImportStatus = z.infer<typeof PoiImportStatusSchema>;
